import React from 'react'
import moment from 'moment'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components'
import { Button } from 'transfix-ui/components/Button'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { shadows } from 'transfix-ui/theme/shadows'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { titleCaseRuleName } from 'apps/carrier/components/Compliance/Cards/CarrierStatus/utils'
import { ValueField } from 'apps/carrier/components/Compliance/ValueField'
import { FP_HOME } from 'apps/fleet-planner/config'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useActivationPageHighwayQuery } from 'apps/fleet-planner/pages/activation/graphql/ActivationPageHighway'
import { capitalize } from 'utils/capitalize'
import formatCurrency from 'utils/formatCurrency'
import { useBreakpoint } from 'utils/hooks'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { FORMATS } from 'constants/datetime'
import { TProvideInsuranceSchema } from './ProvideInsuranceForm/ProvideInsuranceSchema'
import { EActivationStep, EMobileMessage, NO_DATA_LABEL } from './constants'

export interface IContact extends TProvideInsuranceSchema {
  id?: string
}

export function ProvideInsurancePreFilled() {
  const isW9SkipEnabled = useFeatureFlag('fmsSkipW9')
  const { isTablet } = useBreakpoint()
  const {
    carrierDetails,
    hasW9,
    isLoading,
    isSafetyLetterNeeded,
    mobileApp,
    setCurrentStep,
  } = useActivationContext()

  const { fleetPlannerMotorCarrierId = '' } = carrierDetails || {}

  const { data, loading: activationPageQueryLoading } = useActivationPageHighwayQuery({
    variables: { motorCarrierId: fleetPlannerMotorCarrierId },
  })
  const { highwayCarrier } = data || {}
  const { insurancePolicies = [] } = highwayCarrier || {}

  const loading = activationPageQueryLoading || isLoading

  const determineNextStep = () => {
    if (hasW9 && !isSafetyLetterNeeded) {
      postMessageToMobileApp(mobileApp, EMobileMessage.DOCUMENT_UPLOAD_COMPLETE)
      window.location.replace(`${__ROOT__}/fms${FP_HOME}`)
    } else if (hasW9 && !isSafetyLetterNeeded) {
      setCurrentStep(EActivationStep.safetyLetter)
    } else {
      setCurrentStep(EActivationStep.documents)
    }
  }

  // TODO: delete function and isW9SkipEnabled FF when no longer needed
  const onSuccessHandler = () => {
    if (isW9SkipEnabled) {
      determineNextStep()
    } else {
      setCurrentStep(EActivationStep.documents)
    }
  }

  return (
    <Box mb={2.5} mt={1.5}>
      <Box>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6} style={{ borderWidth: 1 }}>
            <Typography variant='h3'>Review Insurance Information</Typography>
          </Grid>
        </Grid>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Box
              display='grid'
              gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'}
              gap='16px'
              justifyContent='center'
              py='24px'
              alignItems='center'>
              {insurancePolicies?.map(
                ({
                  coverageLimit = NO_DATA_LABEL,
                  expirationDate = NO_DATA_LABEL,
                  insurer,
                  isType = NO_DATA_LABEL,
                  policyNumber = NO_DATA_LABEL,
                  status = NO_DATA_LABEL,
                }) => (
                  <Box key={policyNumber}>
                    <Box
                      boxShadow={shadows[2]}
                      height='100%'
                      borderRadius='8px'
                      display='grid'
                      gap='4px'
                      p={2}>
                      <Box display='flex' justifyContent='space-between'>
                        <Typography>{titleCaseRuleName(isType || '')}</Typography>
                        <Tag
                          label={status ? capitalize(status) : 'unknown'}
                          size={TAG_SIZE.xsmall}
                          tagStyle={status === 'active' ? TAG_STYLE.success : TAG_STYLE.info}
                        />
                      </Box>
                      <Box textOverflow='ellipsis' whiteSpace='nowrap'>
                        <ValueField
                          title='Insurer Name'
                          titleColor='textPrimary'
                          value={insurer?.name}
                          fallback={NO_DATA_LABEL}
                          loading={loading}
                        />
                      </Box>
                      <Box textOverflow='ellipsis' whiteSpace='nowrap'>
                        <ValueField
                          title='Policy #'
                          titleColor='textPrimary'
                          value={policyNumber}
                          fallback={NO_DATA_LABEL}
                          loading={loading}
                        />
                      </Box>
                      <Box justifyContent='space-between' alignItems='center'>
                        <ValueField
                          title='Limit'
                          titleColor='textPrimary'
                          value={formatCurrency(coverageLimit)}
                          fallback={NO_DATA_LABEL}
                          loading={loading}
                        />
                        <ValueField
                          title='Expiration Date'
                          titleColor='textPrimary'
                          value={
                            expirationDate && moment(expirationDate).format(FORMATS.dateFormat)
                          }
                          fallback={NO_DATA_LABEL}
                          loading={loading}
                        />
                      </Box>
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid container xs={12} md={8} lg={6} alignItems='center' justifyContent='space-between'>
            <Typography variant='body2'>
              If this information is incorrect,{' '}
              <a href='mailto:carrier-app-support@transfix.io'>contact support</a>
            </Typography>
            <Box>
              <Button
                onClick={onSuccessHandler}
                color='primary'
                size='large'
                data-test='submit-button'>
                Continue
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
